import { useState, useRef, useEffect } from "react";
import { Sun, Moon, Car, ShieldCheck, Mail, Check, X, Clock, ArrowLeft, RotateCcw, Camera, ImageUp, Lock } from "lucide-react";

import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

/* ============================================================
   CONSTANTS
   ============================================================ */

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 36 }, (_, i) => CURRENT_YEAR - i);

const VEHICLE_MAKES = {
  Ford: ["F-150", "F-250", "F-350", "F-450", "Expedition", "Explorer"],
  Chevrolet: ["Silverado 1500", "Silverado 2500HD", "Silverado 3500HD", "Suburban", "Tahoe"],
  GMC: ["Sierra 1500", "Sierra 2500HD", "Sierra 3500HD", "Yukon"],
  RAM: ["1500", "2500", "3500"],
  Toyota: ["Tundra", "Tacoma", "4Runner", "Sequoia"],
  Nissan: ["Titan", "Titan XD", "Frontier"],
  Jeip: ["Grand Cherokee", "Wrangler", "Gladiator"],
  Honda: ["Ridgeline", "Pilot"],
  Freightliner: ["M2"],
  International: ["MV"],
};
const MAKE_OPTIONS = [...Object.keys(VEHICLE_MAKES), "Other"];

const EQUIPMENT_ITEMS = [
  { key: "amber_rotating_beacon", label: "Amber rotating or strobe beacon", icon: "beacon" },
  { key: "oversize_load_sign_front", label: '"OVERSIZE LOAD" sign — front', icon: "sign" },
  { key: "oversize_load_sign_rear", label: '"OVERSIZE LOAD" sign — rear', icon: "sign" },
  { key: "warning_flags", label: "Orange warning flags", icon: "flag" },
  { key: "two_way_radio", label: "CB or two-way radio", icon: "radio" },
  { key: "high_visibility_vest", label: "High-visibility safety vest", icon: "vest" },
  { key: "fire_extinguisher", label: "Fire extinguisher", icon: "extinguisher" },
  { key: "first_aid_kit", label: "First aid kit", icon: "firstaid" },
  { key: "warning_triangles", label: "Warning triangles or road flares", icon: "triangle" },
  { key: "state_cert_card", label: "Current state pilot car certification card", icon: "idcard" },
];

function EquipmentIcon({ type }) {
  const c = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (type) {
    case "beacon": return <svg {...c}><path d="M9 20h6l-1 2h-4z" /><path d="M8 17h8v3H8z" /><circle cx="12" cy="9" r="5" /><path d="M12 2v2M4.5 4.5l1.4 1.4M2 9h2M19.5 4.5l-1.4 1.4M22 9h-2" /></svg>;
    case "sign": return <svg {...c}><rect x="4" y="5" width="16" height="11" rx="1.5" /><path d="M12 8v4M12 14h.01" /><path d="M10 20l2-4 2 4" /></svg>;
    case "flag": return <svg {...c}><path d="M5 3v18" /><path d="M5 4h11l-2 3.5L16 11H5" /></svg>;
    case "radio": return <svg {...c}><rect x="6" y="8" width="12" height="12" rx="1.5" /><path d="M9 8V6a2 2 0 012-2h2a2 2 0 012 2v2" /><path d="M9.5 13h5M9.5 16h3" /></svg>;
    case "vest": return <svg {...c}><path d="M9 3.5L12 5l3-1.5 2 3.5-2 2v11H7V9L5 7z" /><path d="M9 3.5V13M15 3.5V13" /></svg>;
    case "extinguisher": return <svg {...c}><rect x="8.5" y="7" width="7" height="14" rx="2.4" /><path d="M12 7V4M9.5 4h5M6.5 10h2" /></svg>;
    case "firstaid": return <svg {...c}><rect x="4" y="8" width="16" height="11" rx="2" /><path d="M8.5 8V6.5a2 2 0 012-2h3a2 2 0 012 2V8" /><path d="M12 11.5v5M9.5 14h5" /></svg>;
    case "triangle": return <svg {...c}><path d="M12 4l9 16H3z" /><path d="M12 10.5v3.5M12 17h.01" /></svg>;
    case "idcard": return <svg {...c}><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="8.5" cy="12" r="2" /><path d="M13.5 10.5h5M13.5 13.5h5" /></svg>;
    default: return null;
  }
}

/* ============================================================
   STYLE
   ============================================================ */
const CSS = `
.plc-verify {
  --font-display: 'Space Grotesk', 'Inter', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'IBM Plex Mono', 'SFMono-Regular', monospace;
  --amber: #FFB020; --amber-deep: #E0900A; --amber-tint: rgba(255,176,32,0.14);
  --green: #22C55E; --green-tint: rgba(34,197,94,0.14);
  --red: #EF4444; --red-tint: rgba(239,68,68,0.14);
  --blue: #4C8DFF; --blue-tint: rgba(76,141,255,0.14);
  --radius-sm: 8px; --radius-md: 12px; --radius-lg: 18px;
  --bg:#0B1E33; --surface:#14304F; --border:#22405F; --border-soft:#1C3654;
  --text:#EAF1FA; --text-muted:#93A7BD; --text-faint:#6F86A0;
  font-family: var(--font-body); background: var(--bg); color: var(--text);
  min-height: 100%; -webkit-font-smoothing: antialiased;
}
.plc-verify[data-theme="light"] {
  --bg:#F5F6F8; --surface:#FFFFFF; --border:#E3E6EB; --border-soft:#ECEEF1;
  --text:#12151A; --text-muted:#5B6472; --text-faint:#8B93A1;
}
.plc-verify * { box-sizing: border-box; }
.plc-verify h1,.plc-verify h2,.plc-verify h3 { font-family: var(--font-display); letter-spacing:-0.01em; margin:0; }
.plc-verify button { font-family: inherit; }
.plc-verify :focus-visible { outline:2px solid var(--amber); outline-offset:2px; border-radius:6px; }
@media (prefers-reduced-motion: reduce) { .plc-verify * { animation-duration:.001ms !important; transition-duration:.001ms !important; } }

.plc-vh { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }

.plc-v-topbar { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid var(--border-soft); position:sticky; top:0; background:var(--bg); z-index:20; }
.plc-v-brand { display:flex; align-items:center; gap:10px; }
.plc-beacon-mark { width:28px; height:28px; border-radius:50%; background:radial-gradient(circle at 35% 30%, var(--amber) 0%, var(--amber-deep) 70%); box-shadow:0 0 0 4px var(--amber-tint), 0 0 18px rgba(255,176,32,.55); flex-shrink:0; }
.plc-v-brand-text { font-family:var(--font-display); font-weight:700; font-size:16px; line-height:1.2; }
.plc-v-brand-text small { display:block; font-family:var(--font-body); font-weight:500; font-size:10.5px; letter-spacing:.08em; text-transform:uppercase; color:var(--text-faint); margin-top:3px; }
.plc-theme-btn { all:unset; cursor:pointer; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; border:1px solid var(--border); color:var(--text-muted); flex-shrink:0; }
.plc-theme-btn:hover { color:var(--amber); border-color:var(--amber); }

.plc-v-content { padding:28px 20px 60px; max-width:680px; width:100%; margin:0 auto; }
@media (max-width:640px) { .plc-v-content { padding:18px 16px 48px; } }

.plc-card { background:var(--surface); border:1px solid var(--border-soft); border-radius:var(--radius-lg); padding:22px; }
@media (max-width:480px) { .plc-card { padding:18px; border-radius:var(--radius-md); } }

.plc-btn { all:unset; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:12px 18px; border-radius:10px; font-size:13.5px; font-weight:600; }
.plc-btn:active { transform:scale(.98); }
.plc-btn-primary { background:var(--amber); color:#1A1200; }
.plc-btn-primary:hover { background:var(--amber-deep); color:#fff; }
.plc-btn-ghost { background:transparent; border:1px solid var(--border); color:var(--text); }
.plc-btn:disabled { opacity:.45; cursor:not-allowed; }
.plc-btn-block { width:100%; }

.plc-beacon { position:relative; display:inline-flex; width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.plc-beacon::after { content:''; position:absolute; inset:-5px; border-radius:50%; opacity:.35; }
.plc-beacon.amber { background:var(--amber); } .plc-beacon.amber::after { background:var(--amber); animation:plc-pulse 1.6s ease-in-out infinite; }
.plc-beacon.green { background:var(--green); } .plc-beacon.red { background:var(--red); }
@keyframes plc-pulse { 0%,100%{transform:scale(1);opacity:.35;} 50%{transform:scale(1.9);opacity:0;} }

.plc-chip { display:inline-flex; align-items:center; gap:7px; padding:5px 11px; border-radius:999px; font-size:11.5px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; }
.plc-chip-clean { background:var(--green-tint); color:var(--green); }
.plc-chip-review { background:var(--amber-tint); color:var(--amber-deep); }
.plc-verify[data-theme="dark"] .plc-chip-review { color:var(--amber); }
.plc-chip-incomplete { background:var(--red-tint); color:var(--red); }

/* progress bar + stepper */
.plc-progress-wrap { margin-bottom:26px; }
.plc-progress-track { height:6px; background:var(--border-soft); border-radius:999px; overflow:hidden; margin-bottom:16px; }
.plc-progress-fill { height:100%; background:linear-gradient(90deg, var(--amber-deep), var(--amber)); border-radius:999px; transition:width .35s ease; }
.plc-stepper { display:flex; align-items:flex-start; }
.plc-step { flex:1; display:flex; flex-direction:column; gap:10px; }
.plc-step .row { display:flex; align-items:center; gap:10px; }
.plc-step .line { flex:1; height:2px; background:var(--border); }
.plc-step.done .line, .plc-step.current .line { background:var(--amber); }
.plc-step .dot { width:26px; height:26px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:11.5px; font-weight:700; font-family:var(--font-mono); border:2px solid var(--border); color:var(--text-muted); background:var(--surface); }
.plc-step.current .dot { border-color:var(--amber); color:var(--amber); background:var(--amber-tint); }
.plc-step.done .dot { border-color:var(--green); background:var(--green); color:#08210F; }
.plc-step.locked { opacity:.45; }
.plc-step.clickable { cursor:pointer; }
.plc-step.clickable .dot:hover { border-color:var(--amber); color:var(--amber); }
.plc-step.clickable:hover .label { color:var(--amber-deep); }
.plc-verify[data-theme="dark"] .plc-step.clickable:hover .label { color:var(--amber); }
.plc-step .label { font-size:11.5px; font-weight:600; }
.plc-step .label span { display:block; font-weight:400; color:var(--text-muted); font-size:10.5px; margin-top:2px; }
@media (max-width:640px) { .plc-step .label span { display:none; } .plc-step .label { font-size:10px; } }

.plc-panel h2 { font-size:20px; margin-bottom:6px; }
.plc-panel p.hint { color:var(--text-muted); font-size:13.5px; margin:0 0 22px; line-height:1.5; }
.plc-back-btn { all:unset; display:inline-flex; align-items:center; gap:6px; cursor:pointer; font-size:12.5px; font-weight:600; color:var(--text-muted); margin-bottom:14px; }
.plc-back-btn:hover { color:var(--amber-deep); }
.plc-verify[data-theme="dark"] .plc-back-btn:hover { color:var(--amber); }
.plc-field { margin-bottom:14px; }
.plc-field label { display:block; font-size:12.5px; font-weight:600; margin-bottom:6px; }
.plc-field select, .plc-field input[type="text"], .plc-field input[type="email"] { width:100%; background:var(--bg); border:1px solid var(--border); border-radius:10px; color:var(--text); font-family:var(--font-body); font-size:13.5px; padding:10px 12px; appearance:none; }
.plc-field select { background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B93A1' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; padding-right:32px; }
.plc-field select:focus, .plc-field input:focus { border-color:var(--amber); outline:none; }
.plc-field input.mono-input { font-family:var(--font-mono); text-transform:uppercase; }
.plc-row3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px; }
@media (max-width:480px) { .plc-row3 { grid-template-columns:1fr; } }
.plc-row2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
@media (max-width:480px) { .plc-row2 { grid-template-columns:1fr; } }

/* photo upload / doc-preview cards */
.plc-doc-upload { border:1px solid var(--border-soft); border-radius:var(--radius-md); overflow:hidden; margin-bottom:14px; background:var(--surface); }
.plc-doc-upload .label { display:flex; align-items:center; gap:8px; padding:10px 14px; font-size:12px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; color:var(--text-faint); border-bottom:1px solid var(--border-soft); }
.plc-doc-upload .label .req { margin-left:auto; font-size:10.5px; font-weight:600; text-transform:none; letter-spacing:0; color:var(--text-muted); }
.plc-doc-upload .thumb-btn { all:unset; display:block; width:100%; cursor:pointer; }
.plc-doc-upload .thumb { height:128px; display:flex; align-items:center; justify-content:center; flex-direction:column; gap:4px; color:var(--text-muted); font-size:12px; background:repeating-linear-gradient(45deg, var(--bg), var(--bg) 10px, var(--surface) 10px, var(--surface) 20px); }
.plc-doc-upload.filled .thumb { background-size:cover; background-position:center; color:transparent; position:relative; }
.plc-doc-upload.filled .thumb .retake-badge { position:absolute; right:8px; bottom:8px; display:flex; align-items:center; gap:5px; padding:5px 10px; border-radius:999px; background:rgba(10,12,15,.72); color:#fff; font-size:11px; font-weight:600; }
.plc-doc-upload .thumb .hint-text { font-weight:500; }
.plc-doc-upload .doc-hint { padding:8px 14px 11px; font-size:11.5px; color:var(--text-muted); line-height:1.5; border-top:1px solid var(--border-soft); }
.plc-doc-upload .doc-actions { display:flex; gap:1px; background:var(--border-soft); }
.plc-doc-action-row-top { border-top:1px solid var(--border-soft); }
.doc-action-btn { all:unset; flex:1; display:flex; align-items:center; justify-content:center; gap:6px; padding:10px 8px; background:var(--surface); color:var(--text); font-size:12px; font-weight:600; cursor:pointer; text-align:center; }
.doc-action-btn:hover { background:var(--bg); color:var(--amber-deep); }
.plc-verify[data-theme="dark"] .doc-action-btn:hover { color:var(--amber); }
.plc-doc-upload.has-error { border-color:var(--red); }
.doc-error { display:flex; align-items:flex-start; gap:6px; padding:9px 14px 12px; font-size:11.5px; color:var(--red); line-height:1.5; border-top:1px solid var(--border-soft); }

.plc-info-card { display:flex; gap:12px; padding:14px 16px; border-radius:var(--radius-md); background:var(--blue-tint); border:1px solid transparent; margin-bottom:16px; }
.plc-info-card svg { flex-shrink:0; color:var(--blue); margin-top:1px; }
.plc-info-card .t { font-size:12.5px; font-weight:600; margin-bottom:4px; }
.plc-info-card ul { margin:0; padding-left:16px; font-size:12.5px; color:var(--text-muted); line-height:1.6; }

.plc-checklist { display:flex; flex-direction:column; gap:2px; margin:4px 0 16px; border:1px solid var(--border-soft); border-radius:var(--radius-md); overflow:hidden; }
.plc-checklist-item { display:flex; align-items:center; gap:12px; padding:11px 14px; background:var(--surface); border-bottom:1px solid var(--border-soft); cursor:pointer; }
.plc-checklist-item:last-child { border-bottom:none; }
.plc-checklist-item:hover { background:var(--bg); }
.plc-checklist-item .icon-swatch { width:34px; height:34px; border-radius:9px; background:var(--amber-tint); color:var(--amber-deep); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.plc-verify[data-theme="dark"] .plc-checklist-item .icon-swatch { color:var(--amber); }
.plc-checklist-item input { width:17px; height:17px; accent-color:var(--amber); cursor:pointer; flex-shrink:0; margin-left:auto; }
.plc-checklist-item span.label { font-size:13px; flex:1; }

.plc-reqlist { display:flex; flex-direction:column; gap:2px; border:1px solid var(--border-soft); border-radius:var(--radius-md); overflow:hidden; margin-bottom:18px; }
.plc-reqrow { display:flex; align-items:center; gap:12px; padding:12px 14px; background:var(--surface); border-bottom:1px solid var(--border-soft); }
.plc-reqrow:last-child { border-bottom:none; }
.plc-reqrow .ic { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.plc-reqrow .ic.done { background:var(--green-tint); color:var(--green); }
.plc-reqrow .ic.missing { background:var(--red-tint); color:var(--red); }
.plc-reqrow .body { flex:1; min-width:0; }
.plc-reqrow .t { font-size:13px; font-weight:600; }
.plc-reqrow .s { font-size:11.5px; color:var(--text-muted); margin-top:1px; }
.plc-reqrow .jump { all:unset; cursor:pointer; font-size:11.5px; font-weight:700; color:var(--amber-deep); }
.plc-verify[data-theme="dark"] .plc-reqrow .jump { color:var(--amber); }

.plc-result-card { text-align:center; padding:36px 24px; }
@media (max-width:480px) { .plc-result-card { padding:26px 16px; } }
.plc-result-card .big-beacon { width:64px; height:64px; border-radius:50%; margin:0 auto 18px; display:flex; align-items:center; justify-content:center; }
.plc-result-card h2 { font-size:21px; margin-bottom:8px; }
.plc-result-card p { color:var(--text-muted); font-size:13.5px; max-width:420px; margin:0 auto 20px; line-height:1.55; }
.plc-signal-list { display:flex; flex-direction:column; gap:8px; margin-top:4px; text-align:left; }
.plc-signal-row { display:flex; align-items:flex-start; gap:10px; padding:10px 12px; border-radius:10px; background:var(--bg); border:1px solid var(--border-soft); }
.plc-signal-row .plc-beacon { margin-top:5px; }
.plc-signal-row .st { font-size:12.5px; font-weight:600; }
.plc-signal-row .rs { font-size:12px; color:var(--text-muted); margin-top:1px; }
.plc-section-title { font-size:12.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-faint); margin:18px 0 10px; }
.plc-section-title:first-child { margin-top:0; }

.plc-trust-bar { display:flex; align-items:flex-start; gap:9px; padding:11px 14px; margin-bottom:18px; border-radius:10px; background:var(--blue-tint); font-size:11.5px; line-height:1.55; color:var(--text-muted); }
.plc-trust-bar svg { flex-shrink:0; color:var(--blue); margin-top:1.5px; }
.plc-trust-bar strong { color:var(--text); font-weight:600; }

.plc-email-card { display:flex; gap:12px; text-align:left; padding:16px; border-radius:var(--radius-md); background:var(--bg); border:1px solid var(--border-soft); margin-top:18px; }
.plc-email-card svg { flex-shrink:0; color:var(--blue); margin-top:2px; }
.plc-email-card .t { font-size:13px; font-weight:700; margin-bottom:3px; }
.plc-email-card .s { font-size:12px; color:var(--text-muted); line-height:1.5; }
.plc-email-card .addr { font-family:var(--font-mono); color:var(--text); }
.plc-email-actions { display:flex; gap:10px; margin-top:12px; flex-wrap:wrap; }
`;

const STEPS = [
  { n: 1, label: "Identity", sub: "Stripe Identity" },
  { n: 2, label: "Vehicle", sub: "Year, make, VIN, equipment" },
  { n: 3, label: "Documents", sub: "License, insurance, registration" },
  { n: 4, label: "Review", sub: "Confirm before submitting" },
  { n: 5, label: "Submitted", sub: "Verification in progress" },
  { n: 6, label: "Status", sub: "Verified or in review", locked: true },
];

function readAsDataURL(file, cb) {
  const reader = new FileReader();
  reader.onload = () => cb(reader.result);
  reader.readAsDataURL(file);
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

function formatFileSize(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function DocUpload({ id, title, required, hint, fileMeta, onChange }) {
  const [error, setError] = useState(null);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  function processFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("That file isn't a photo. Please choose a JPG or PNG image.");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`This photo is ${formatFileSize(file.size)}, which is over our 5MB limit. Please retake it at a lower resolution or choose a smaller file.`);
      return;
    }
    setError(null);
    readAsDataURL(file, (preview) => onChange({ name: file.name, preview, size: file.size }));
  }

  return (
    <div className={`plc-doc-upload${fileMeta ? " filled" : ""}${error ? " has-error" : ""}`}>
      <div className="label">
        {title}
        <span className="req">{required ? "Required" : "Optional"}</span>
      </div>

      <button type="button" className="thumb-btn" onClick={() => libraryInputRef.current && libraryInputRef.current.click()}>
        <div
          className="thumb"
          style={fileMeta ? { backgroundImage: `url(${fileMeta.preview})` } : undefined}
        >
          {!fileMeta && <span className="hint-text">No photo selected yet</span>}
          {fileMeta && (
            <span className="retake-badge">
              <RotateCcw size={12} /> Retake
            </span>
          )}
        </div>
      </button>

      <div className="doc-actions">
        <button type="button" className="doc-action-btn" onClick={() => cameraInputRef.current && cameraInputRef.current.click()}>
          <Camera size={14} /> Take Photo
        </button>
        <button type="button" className="doc-action-btn" onClick={() => libraryInputRef.current && libraryInputRef.current.click()}>
          <ImageUp size={14} /> Upload Photo
        </button>
      </div>

      {error ? (
        <div className="doc-error">{error}</div>
      ) : (
        hint && <div className="doc-hint">{fileMeta ? "Photo selected — tap the image or \u201cRetake Photo\u201d to replace it." : hint}</div>
      )}

      <input
        ref={cameraInputRef}
        id={`${id}-camera`}
        type="file"
        accept="image/*"
        capture="environment"
        className="plc-vh"
        onChange={(e) => processFile(e.target.files[0])}
      />
      <input
        ref={libraryInputRef}
        id={id}
        type="file"
        accept="image/*"
        className="plc-vh"
        onChange={(e) => processFile(e.target.files[0])}
      />
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function VerificationTab() {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, otherwise default to "light"
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme) {
        return savedTheme;
      }
    }
    return "light";
  });

  // Sync theme changes to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", theme);
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Boolean helper to pass to Navbar and Footer based on the current theme state
  const isDarkMode = theme === "dark";

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [vehicle, setVehicle] = useState({ year: "", make: "", customMake: "", model: "", customModel: "", vin: "" });
  const [vinPhoto, setVinPhoto] = useState(null);
  const [equipmentChecked, setEquipmentChecked] = useState({});

  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [insuranceDoc, setInsuranceDoc] = useState(null);
  const [registrationDoc, setRegistrationDoc] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [emailResent, setEmailResent] = useState(false);

  const modelOptions = vehicle.make && VEHICLE_MAKES[vehicle.make] ? [...VEHICLE_MAKES[vehicle.make], "Other"] : [];

  const identityComplete = step > 1;
  const vehicleComplete =
    !!(vehicle.year &&
      (vehicle.make === "Other" ? vehicle.customMake.trim() : vehicle.make) &&
      (vehicle.make === "Other" || vehicle.model === "Other" ? vehicle.customModel.trim() : vehicle.model) &&
      vehicle.vin.trim().length >= 11 &&
      vinPhoto);
  const licenseComplete = !!(licenseFront && licenseBack);
  const insuranceComplete = !!insuranceDoc;
  const registrationComplete = !!registrationDoc;
  const documentsComplete = licenseComplete && insuranceComplete && registrationComplete;

  const requirements = [
    { key: "identity", label: "Identity verification", sub: "Confirmed via Stripe Identity", done: identityComplete, jumpTo: 1 },
    { key: "vehicle", label: "Vehicle information", sub: "Year, make, model, VIN photo", done: vehicleComplete, jumpTo: 2 },
    { key: "license", label: "Driver's license", sub: "Front and back photos", done: licenseComplete, jumpTo: 3 },
    { key: "insurance", label: "Insurance card", sub: "Current policy, expiration visible", done: insuranceComplete, jumpTo: 3 },
    { key: "registration", label: "Vehicle registration", sub: "VIN and expiration visible", done: registrationComplete, jumpTo: 3 },
  ];
  const allComplete = requirements.every((r) => r.done);

  function handleSubmit() {
    setStep(5);
    setSubmitting(true);
    
    // Hardcoded to strictly demonstrate the error states for your client.
    setTimeout(() => {
      const result = {
        status: "ACTION_NEEDED",
        signals: [
          { type: "VEHICLE_VIN_MATCH", status: "INVALID", reason: "Invalid vehicle number. The VIN in the photo does not match the entered data." },
          { type: "INSURANCE_VALIDATION", status: "EXPIRED", reason: "Invalid or expired insurance. Please upload a current policy." },
        ],
      };
      
      setOutcome(result);
      setSubmitting(false);
      setStep(6);
    }, 1400);
  }

  function signalColor(status) {
    if (status === "OK") return "green";
    if (status === "EXPIRED" || status === "EDITED" || status === "INVALID") return "red";
    return "amber";
  }

  function statusChip(status) {
    if (status === "VERIFIED") return <span className="plc-chip plc-chip-clean">Clean match</span>;
    if (status === "IN_REVIEW") return <span className="plc-chip plc-chip-review">In review</span>;
    return <span className="plc-chip plc-chip-incomplete">Action needed</span>;
  }

  const progressPct = Math.min(100, ((step - 1) / (STEPS.length - 1)) * 100);

  return (
    <div className="plc-verify" data-theme={theme}>
      <style>{CSS}</style>

      {/* Insert Navbar at the top of the app wrapper */}
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

    

      <div className="plc-v-content">
        <div className="plc-progress-wrap">
          <div className="plc-progress-track">
            <div className="plc-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="plc-stepper">
            {STEPS.map((s, i) => {
              const canJump = !s.locked && s.n < step && step <= 4;
              return (
                <div
                  key={s.n}
                  className={`plc-step${s.locked ? " locked" : s.n < step ? " done" : s.n === step ? " current" : ""}${canJump ? " clickable" : ""}`}
                  style={s.n === 6 ? { flex: 0.6 } : undefined}
                  onClick={canJump ? () => setStep(s.n) : undefined}
                  role={canJump ? "button" : undefined}
                  tabIndex={canJump ? 0 : undefined}
                >
                  <div className="row">
                    <div className="dot">{!s.locked && s.n < step ? <Check size={13} /> : s.n}</div>
                    {i < STEPS.length - 1 && <div className="line" />}
                  </div>
                  <div className="label">{s.label}<span>{s.sub}</span></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="plc-trust-bar">
          <Lock size={14} />
          <span>
            <strong>Your information is protected.</strong> Every photo you upload is encrypted in transit and stored securely.
            Your identity check in Step 1 is handled directly by Stripe Identity, a PCI DSS Level&nbsp;1 certified
            platform used by companies worldwide — PilotLinkUSA never stores your raw photo ID.
          </span>
        </div>

        {step === 1 && (
          <div className="plc-card plc-panel">
            <h2>Verify your identity</h2>
            <p className="hint">We use Stripe Identity to confirm you are who you say you are — a photo ID and a quick selfie check, usually done in under two minutes.</p>
            <div className="plc-field">
              <label htmlFor="vEmail">Email</label>
              <input id="vEmail" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <button className="plc-btn plc-btn-primary plc-btn" onClick={() => setStep(2)}>Start identity verification</button>
          </div>
        )}

        {step === 2 && (
          <div className="plc-card plc-panel">
            <button className="plc-back-btn" onClick={() => setStep(1)}><ArrowLeft size={13} /> Back</button>
            <h2>Tell us about your vehicle</h2>
            <p className="hint">We match this against your insurance and registration to confirm everything belongs to the same vehicle.</p>

            <div className="plc-row3">
              <div className="plc-field">
                <label htmlFor="vYear">Year</label>
                <select id="vYear" value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })}>
                  <option value="">Select year</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="plc-field">
                <label htmlFor="vMake">Make</label>
                <select id="vMake" value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value, model: "", customModel: "" })}>
                  <option value="">Select make</option>
                  {MAKE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="plc-field">
                <label htmlFor="vModel">Model</label>
                {vehicle.make === "Other" ? (
                  <input id="vModel" type="text" placeholder="Model" value={vehicle.customModel} onChange={(e) => setVehicle({ ...vehicle, customModel: e.target.value })} />
                ) : (
                  <select id="vModel" value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} disabled={!vehicle.make}>
                    <option value="">{vehicle.make ? "Select model" : "Select make first"}</option>
                    {modelOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                )}
              </div>
            </div>

            {vehicle.make === "Other" && (
              <div className="plc-field">
                <label htmlFor="vCustomMake">Make (type it in)</label>
                <input id="vCustomMake" type="text" placeholder="e.g. Peterbilt" value={vehicle.customMake} onChange={(e) => setVehicle({ ...vehicle, customMake: e.target.value })} />
              </div>
            )}
            {vehicle.make !== "Other" && vehicle.model === "Other" && (
              <div className="plc-field">
                <label htmlFor="vCustomModel">Model (type it in)</label>
                <input id="vCustomModel" type="text" placeholder="Model" value={vehicle.customModel} onChange={(e) => setVehicle({ ...vehicle, customModel: e.target.value })} />
              </div>
            )}

            <div className="plc-field">
              <label htmlFor="vVin">VIN</label>
              <input id="vVin" className="mono-input" type="text" maxLength={17} placeholder="1HGCM82633A004352"
                value={vehicle.vin} onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase() })} />
            </div>

            <div className="plc-info-card">
              <Car size={18} />
              <div>
                <div className="t">Where to find your VIN</div>
                <ul>
                  <li>Lower corner of the dashboard on the driver's side — visible through the windshield from outside the vehicle.</li>
                  <li>On a sticker inside the driver's door frame, visible when the door is open.</li>
                </ul>
              </div>
            </div>

            <DocUpload
              id="vinPhotoInput"
              title="Photo of your VIN"
              required
              hint="Capture all 17 characters in a single, well-lit shot — no glare, no blur, no cropping. JPG or PNG, up to 5MB."
              fileMeta={vinPhoto}
              onChange={setVinPhoto}
            />

            <div className="plc-section-title">Equipment on board</div>
            <div className="plc-checklist">
              {EQUIPMENT_ITEMS.map((item) => (
                <label className="plc-checklist-item" key={item.key}>
                  <span className="icon-swatch"><EquipmentIcon type={item.icon} /></span>
                  <span className="label">{item.label}</span>
                  <input
                    type="checkbox"
                    checked={!!equipmentChecked[item.key]}
                    onChange={(e) => setEquipmentChecked({ ...equipmentChecked, [item.key]: e.target.checked })}
                  />
                </label>
              ))}
            </div>

            <button className="plc-btn plc-btn-primary plc-btn" disabled={!vehicleComplete} onClick={() => setStep(3)} style={{ marginTop: 6 }}>
              Save vehicle info
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="plc-card plc-panel">
            <button className="plc-back-btn" onClick={() => setStep(2)}><ArrowLeft size={13} /> Back</button>
            <h2>Upload your documents</h2>
            <p className="hint">Take clear, unedited photos — no glare, no crops. We'll confirm the details match the vehicle information you provided.</p>

            <div className="plc-info-card">
              <Lock size={18} />
              <div>
                <div className="t">How these documents are handled</div>
                <ul>
                  <li>Uploaded over an encrypted connection and stored securely — never shared or sold.</li>
                  <li>Visible only to your assigned verification specialist, for confirming your documents.</li>
                </ul>
              </div>
            </div>

            <div className="plc-section-title">Driver's license</div>
            <div className="plc-row2">
              <DocUpload
                id="licenseFrontInput"
                title="Front"
                required
                hint="Your name, photo, license number, and expiration date must all be clearly visible. JPG or PNG, up to 5MB."
                fileMeta={licenseFront}
                onChange={setLicenseFront}
              />
              <DocUpload
                id="licenseBackInput"
                title="Back"
                required
                hint="The barcode must be fully visible and unobstructed for automatic verification. JPG or PNG, up to 5MB."
                fileMeta={licenseBack}
                onChange={setLicenseBack}
              />
            </div>

            <div className="plc-section-title">Insurance card</div>
            <DocUpload
              id="insuranceInput"
              title="Insurance card"
              required
              hint="Policy number, insurer name, and expiration date must be clearly visible. Digital copies accepted. JPG or PNG, up to 5MB."
              fileMeta={insuranceDoc}
              onChange={setInsuranceDoc}
            />

            <div className="plc-section-title">Vehicle registration</div>
            <DocUpload
              id="registrationInput"
              title="Registration"
              required
              hint="VIN, license plate, and expiration date must be clearly visible and match your vehicle information. JPG or PNG, up to 5MB."
              fileMeta={registrationDoc}
              onChange={setRegistrationDoc}
            />

            <button className="plc-btn plc-btn-primary plc-btn" disabled={!documentsComplete} onClick={() => setStep(4)} style={{ marginTop: 6 }}>
              Continue to review
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="plc-card plc-panel">
            <button className="plc-back-btn" onClick={() => setStep(3)}><ArrowLeft size={13} /> Back</button>
            <h2>Review before submitting</h2>
            <p className="hint">
              {allComplete
                ? "Everything looks complete. Some submissions are approved automatically; others go to a specialist for a manual check — either way, you'll get an update by email."
                : "A few items still need your attention before you can submit."}
            </p>

            <div className="plc-reqlist">
              {requirements.map((r) => (
                <div className="plc-reqrow" key={r.key}>
                  <span className={`ic ${r.done ? "done" : "missing"}`}>{r.done ? <Check size={14} /> : <X size={14} />}</span>
                  <div className="body">
                    <div className="t">{r.label}</div>
                    <div className="s">{r.done ? r.sub : "Not completed yet"}</div>
                  </div>
                  {!r.done && <button className="jump" onClick={() => setStep(r.jumpTo)}>Fix</button>}
                </div>
              ))}
            </div>

            <button className="plc-btn plc-btn-primary plc-btn" disabled={!allComplete} onClick={handleSubmit}>
              {allComplete ? "Submit for verification" : "Complete required items to submit"}
            </button>
          </div>
        )}

        {step === 5 && (
          <div className="plc-card plc-panel plc-result-card">
            <div className="big-beacon" style={{ background: "var(--amber-tint)" }}>
              <span className="plc-beacon amber" style={{ width: 16, height: 16 }} />
            </div>
            <h2>Confirming your submission…</h2>
            <p>Reading your documents and cross-checking the vehicle information you provided. This usually takes a few seconds.</p>
          </div>
        )}

        {step === 6 && outcome && (
          <div className="plc-card plc-panel plc-result-card">
            <div
              className="big-beacon"
              style={{
                background: outcome.status === "VERIFIED" ? "var(--green-tint)" : outcome.status === "IN_REVIEW" ? "var(--amber-tint)" : "var(--red-tint)",
                color: outcome.status === "VERIFIED" ? "var(--green)" : outcome.status === "IN_REVIEW" ? "var(--amber-deep)" : "var(--red)",
              }}
            >
              {outcome.status === "ACTION_NEEDED" ? <Clock size={28} /> : <ShieldCheck size={28} />}
            </div>
            <h2>
              {outcome.status === "VERIFIED" && "Thank you for submitting"}
              {outcome.status === "IN_REVIEW" && "Thank you for submitting"}
              {outcome.status === "ACTION_NEEDED" && "We need one more thing from you"}
            </h2>
            <p>
              {outcome.status === "VERIFIED" &&
                "Your documents passed every automated check, including your vehicle VIN. A verification specialist will complete a final review, and we'll email you as soon as your Verified Badge is active — usually within 24 hours."}
              {outcome.status === "IN_REVIEW" &&
                "One or two details need a closer look, so we've routed your submission to a specialist for manual review. This is a normal part of the process, not a rejection — you'll receive an email as soon as review is complete, typically within 24–48 hours. No action is needed from you right now."}
              {outcome.status === "ACTION_NEEDED" &&
                "One of your documents couldn't be verified automatically. Update it below and resubmit — everything else you uploaded is saved, and we'll email you as soon as the updated document has been reviewed."}
            </p>
            <div style={{ marginBottom: 14 }}>{statusChip(outcome.status)}</div>
            <div className="plc-signal-list">
              {outcome.signals.map((s, i) => (
                <div className="plc-signal-row" key={i}>
                  <span className={`plc-beacon ${signalColor(s.status)}`} />
                  <div><div className="st">{s.type.replaceAll("_", " ")}</div><div className="rs">{s.reason || "No issues found"}</div></div>
                </div>
              ))}
            </div>

            <div className="plc-email-card">
              <Mail size={18} />
              <div>
                <div className="t">Confirmation sent</div>
                <div className="s">
                  We emailed a receipt and status link to <span className="addr">{email || "your account email"}</span>. We'll email you again the moment your review is complete — you don't need to check back or log in.
                </div>
                <div className="plc-email-actions">
                  <a className="plc-btn plc-btn-ghost" href={`mailto:${email || ""}`}>Open email</a>
                  <button className="plc-btn plc-btn-ghost" onClick={() => setEmailResent(true)}>
                    {emailResent ? "Email resent ✓" : "Resend email"}
                  </button>
                </div>
              </div>
            </div>

            {outcome.status === "ACTION_NEEDED" && (
              <button className="plc-btn plc-btn-primary" style={{ marginTop: 18 }} onClick={() => setStep(3)}>Update document</button>
            )}
          </div>
        )}
      </div>
      
      {/* Insert Footer at the bottom of the app wrapper */}
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}
